import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight, FileUp, Keyboard, X } from 'lucide-react';
import { INK } from '../abf/onboarding/review/reviewStyles';
import { PC_DOCUMENT_ENTRY, PC_MANUAL_ENTRY } from './routes';

/**
 * One way in, two lanes (the ABF onboarding standard, PC workflow parity slice 1): a private credit
 * facility arrives as a credit agreement PDF or is typed in. Both lanes end in the same loan, the
 * same security and the same acquisition ticket; neither is a prerequisite for the other.
 */

const Scrim = styled.div`
  position: fixed; inset: 0; z-index: 60; display: grid; place-items: center; padding: 20px;
  background: rgba(3, 8, 16, .66);
`;

const Panel = styled.div`
  width: min(820px, 100%); border: 1px solid ${INK.lineStrong}; border-radius: 14px;
  background: ${INK.panel}; color: ${INK.body}; box-shadow: 0 24px 64px #000a;
`;

const Head = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  padding: 18px 20px 14px; border-bottom: 1px solid ${INK.line};
  h2 { margin: 0; font-size: 17px; color: ${INK.strong}; }
  p { margin: 6px 0 0; color: ${INK.muted}; font-size: 13px; max-width: 60ch; }
`;

const Close = styled.button`
  appearance: none; border: 1px solid ${INK.line}; background: transparent; color: ${INK.muted};
  border-radius: 8px; width: 30px; height: 30px; display: grid; place-items: center; cursor: pointer;
  &:hover { color: ${INK.body}; border-color: ${INK.lineStrong}; }
`;

const Lanes = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 18px 20px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const Lane = styled.button`
  text-align: left; appearance: none; cursor: pointer; display: flex; flex-direction: column; gap: 8px;
  padding: 16px; border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.raised}; color: ${INK.body};
  &:hover, &:focus-visible { border-color: ${INK.accent}; outline: none; }
  .icon { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; color: ${INK.accent}; background: rgba(103, 198, 244, .12); }
  strong { font-size: 14px; color: ${INK.strong}; }
  span { font-size: 12.5px; color: ${INK.muted}; line-height: 1.45; }
  .go { margin-top: auto; display: inline-flex; align-items: center; gap: 6px; color: ${INK.accent}; font-weight: 700; }
`;

const Foot = styled.p`
  margin: 0; padding: 12px 20px 16px; color: ${INK.faint}; font-size: 12px; border-top: 1px solid ${INK.line};
`;

const NewFacilityLanes: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const go = (path: string) => { onClose(); navigate(path); };
  return (
    <Scrim role="dialog" aria-modal="true" aria-label="New private credit facility"
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={event => { if (event.key === 'Escape') onClose(); }}>
      <Panel>
        <Head>
          <div>
            <h2>New private credit facility</h2>
            <p>Both routes create the same loan, its security and a draft acquisition ticket, with the same
              covenants. Choose the one that matches what you have in front of you.</p>
          </div>
          <Close type="button" onClick={onClose} aria-label="Close"><X size={14} /></Close>
        </Head>
        <Lanes>
          <Lane type="button" autoFocus onClick={() => go(PC_DOCUMENT_ENTRY)}>
            <span className="icon"><FileUp size={16} /></span>
            <strong>I have the executed credit agreement</strong>
            <span>Upload the PDF. The borrower, commitment, pricing, payment terms and covenants are read
              through the Credit Agreement template with their page and clause, and you confirm them in review
              before anything books.</span>
            <span className="go">Upload the agreement <ArrowRight size={12} /></span>
          </Lane>
          <Lane type="button" onClick={() => go(PC_MANUAL_ENTRY)}>
            <span className="icon"><Keyboard size={16} /></span>
            <strong>I will enter the facility</strong>
            <span>Type the borrower, the facility and its terms, and at least one covenant (or acknowledge
              a covenant-lite package). No PDF is required, and nothing about the facility is limited by its
              absence.</span>
            <span className="go">Enter the facility <ArrowRight size={12} /></span>
          </Lane>
        </Lanes>
        <Foot>Either way the acquisition is a ticket: pre-clear, approval, execution and posting are the
          same steps, and the facility is exposure only once the ticket posts.</Foot>
      </Panel>
    </Scrim>
  );
};

export default NewFacilityLanes;
